import React, { useLayoutEffect, useRef } from 'react';
import styles from './TruncatedText.module.scss';

function fitAndTruncateIfNeeded(
  container: HTMLElement,
  value: string,
  options: {
    ellipsis: string;
    keepLast: number;
    truncate?: boolean;
  },
) {
  const element = container;
  const { keepLast, ellipsis } = options;

  const cutIndex = Math.min(value.length, Math.max(1, value.length - keepLast));
  const firstPart = value.slice(0, cutIndex);
  const lastPart = value.slice(cutIndex);

  if (options.truncate) {
    element.textContent = `${firstPart}${ellipsis}${lastPart}`;
  } else {
    element.textContent = value;
  }

  const rect = element.getBoundingClientRect();

  if (element.scrollWidth > Math.round(rect.width)) {
    const nextValue = `${firstPart.slice(0, firstPart.length - 1)}${lastPart}`;
    if (nextValue !== value) {
      fitAndTruncateIfNeeded(element, nextValue, {
        ...options,
        truncate: true,
      });
    }
  }
}

const ELLIPSIS = '[…]';

interface TruncatedTextProps {
  children: string;
  keepLast?: number;
}

const TruncatedText = ({
  children: value,
  keepLast = 0,
}: TruncatedTextProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const container = spanRef.current;

    if (container) {
      const onResize = () =>
        fitAndTruncateIfNeeded(container, value, {
          keepLast,
          ellipsis: ELLIPSIS,
        });

      const observer = new ResizeObserver(onResize);
      onResize();
      observer.observe(container);

      return () => observer.disconnect();
    }

    return undefined;
  }, [value, keepLast]);

  return <span className={styles.truncatedText} ref={spanRef} />;
};

export default TruncatedText;
